import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/lib/nav";

import Pill from "@buildpy/ui/pill";
import { cn } from "@/lib/utils";
import type { ProjectStatus } from "@buildpy/core/modules/project/types";
import PhaseDelayPill from "@/modules/project/ui/phase-delay-pill";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";

interface Props {
  projectId: string;
  title: string;
  status: ProjectStatus;
  date: string;
  isToday: boolean;
  // 착공일부터 센 날 — 착공일이 D+0 이다. 착공 전이면 null.
  daysSinceStart: number | null;
  activePhases: readonly { id: string; name: string }[];
  prevDate: string | null;
  nextDate: string | null;
  // 지금 늦어지는 공정 수(delayedPhaseCount) — 0 이면 안 보인다.
  delayCount?: number;
}

const hrefOf = (projectId: string, date: string): string =>
  `/app/projects/${encodeURIComponent(projectId)}/today?date=${date}`;

const navClass = "inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border";

// 오늘 화면 머리 — 현장명 · 날짜(앞뒤 날로 넘김) · D+n · 진행 중 공정.
const TodayHeader = ({
  projectId,
  title,
  status,
  date,
  isToday,
  daysSinceStart,
  activePhases,
  prevDate,
  nextDate,
  delayCount = 0,
}: Props) => (
  <header className="flex flex-col gap-y-2" data-testid="today-header">
    <div className="flex min-w-0 items-center gap-x-2">
      <h1 className="min-w-0 truncate text-lg font-semibold">
        <Link
          className="underline-offset-2 hover:underline"
          data-testid="today-project-link"
          href={`/app/projects/${encodeURIComponent(projectId)}`}>
          {title || "현장"}
        </Link>
      </h1>
      <ProjectStatusBadge className="shrink-0" status={status} />
      <PhaseDelayPill count={delayCount} testId="today-phase-delay" />
    </div>

    <div className="flex items-center gap-x-2">
      {prevDate ? (
        <Link aria-label="전날" className={navClass} href={hrefOf(projectId, prevDate)}>
          <ChevronLeft className="size-4" />
        </Link>
      ) : (
        <span aria-hidden className={cn(navClass, "opacity-30")}>
          <ChevronLeft className="size-4" />
        </span>
      )}
      <div className="flex min-w-0 flex-1 items-center justify-center gap-x-2">
        <span className="text-base font-semibold tabular-nums" data-testid="today-date">
          {date}
        </span>
        {isToday && <span className="text-xs text-muted-foreground">오늘</span>}
        {daysSinceStart !== null && (
          <span
            className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground"
            data-testid="today-d-plus">
            D+{daysSinceStart}
          </span>
        )}
      </div>
      {nextDate ? (
        <Link aria-label="다음날" className={navClass} href={hrefOf(projectId, nextDate)}>
          <ChevronRight className="size-4" />
        </Link>
      ) : (
        <span aria-hidden className={cn(navClass, "opacity-30")}>
          <ChevronRight className="size-4" />
        </span>
      )}
    </div>

    {activePhases.length > 0 && (
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">진행 중</span>
        {activePhases.map((phase) => (
          <Pill key={phase.id} tone="progress">{phase.name}</Pill>
        ))}
      </div>
    )}
  </header>
);

export default TodayHeader;
